import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgSelectModule, NgSelectComponent } from '@ng-select/ng-select';
import { formatDistanceToNow } from 'date-fns';

import { DataService } from '@/services/data.service';
import { StateService } from '@/services/state.service';
import { BidTagsService } from '@/services/bid-tags.service';
import { Web3Service } from '@/services/web3.service';
import { PlatformService } from '@/services/platform.service';

import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, finalize, map, skip, switchMap, take, tap, takeUntil } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { formatEther, keccak256, stringToBytes } from 'viem';

import { PunkGridViewComponent } from '@/components/punk-grid-view/punk-grid-view.component';

import { Filters, Sorts } from '@/models/pipes';
import { Punk } from '@/models/graph';
import { environment } from 'src/environments/environment';
import { LoaderComponent } from '@/components/icons/loader.component';
import { WalletAddressDirective } from '@/directives/wallet-address.directive';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';
import { TippyDirective } from '@/directives/tippy.directive';
import { AddressAvatarComponent } from '@/components/address-avatar/address-avatar.component';
import { BidDetailsModalComponent } from '@/components/bid-details-modal/bid-details-modal.component';
import { BidCreationModalComponent, BidType, CopyFromBidPayload } from '@/components/bid-creation-modal/bid-creation-modal.component';
import { BidCopyConfirmationModalComponent } from '@/components/bid-copy-confirmation-modal/bid-copy-confirmation-modal.component';
import { normalizeStashFundedForUi } from '@/utils/stash-funded';
import {
  buildCompactPaginationItems,
  type CompactPaginationItem,
} from '@/utils/compact-pagination';
import { StashBidsTableComponent } from '@/components/stash-bids-table/stash-bids-table.component';
import {
  mapPunksmarketIndexerBidToUnified,
  unifiedBidToStashTableShape,
} from '@/utils/unified-market-bid.mappers';

interface StashBid {
  id: string;
  bidder_address: string;
  punk_indices?: string;
  punk_indices_count?: number;
  punkIndices?: number[];
  selected_punk_ids?: number[] | string;
  tag?: string;
  tags?: any;
  bid_amount_wei: string;
  bid_amount_eth: number;
  merkle_root?: string;
  account_nonce?: number;
  bid_nonce?: number;
  status?: string;
  created_at: string;
  expires_at?: string;
  signature?: string;
  source?: string;
  decodedTags?: string[];
  bidType?: 'trait' | 'collection' | 'custom' | 'owner' | 'legacy' | 'punksmarket';
  bidTypeLabel?: string;
  stashAddress?: string;
  ownerAddress?: string;
  confidential?: boolean;
  log?: string | null;
  /** 0 = underfunded (show pill), 1 = funded. */
  stash_funded?: 0 | 1;
  biddingOn?: string;
  firstPunkId?: number;
  punkIds?: number[];
  // Precomputed for template rendering (avoid array slice in HTML)
  punkPreviewIds?: number[];
  punkExtraCount?: number;
  /** Display label for bid status column (e.g. Active, Expired, Cancelled). */
  uiStatusLabel?: string;
  /** Filter key used by status dropdown (active, expired, cancelled). */
  uiStatusFilter?: string;
  platform?: 'stash' | 'punksmarket' | 'legacy';
  punksmarketBidId?: string;
  isLegacyBid?: boolean;
  legacyBid?: unknown;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    NgSelectModule,
    PunkGridViewComponent,
    LoaderComponent,
    WalletAddressDirective,
    LazyLoadImageModule,
    TokenIdParsePipe,
    TippyDirective,
    AddressAvatarComponent,
    BidDetailsModalComponent,
    BidCreationModalComponent,
    BidCopyConfirmationModalComponent,
    StashBidsTableComponent,
  ],
  selector: 'app-market',
  templateUrl: './market.component.html',
  styleUrls: ['./market.component.scss'],
})

export class MarketComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  private punkData = new BehaviorSubject<Punk[]>([]);
  punkData$ = this.punkData.asObservable();

  private marketType = new BehaviorSubject<Filters | null>(null);
  marketType$ = this.marketType.asObservable();
  activeSort:Sorts = 'price-low';
  
  // Flag to hide content during redirect
  isRedirecting = false;

  // Stash bids properties
  allStashBids: StashBid[] = [];
  individualStashBids: StashBid[] = [];
  stashBidsLoading = false;
  stashBidsError: string | null = null;
  stashBidsCurrentPage = 1;
  stashBidsItemsPerPage = 10;
  stashBidsTotalPages = 1;
  /** Compact pagination strip (see buildCompactPaginationItems). */
  stashBidsPaginationItems: CompactPaginationItem[] = [];
  lastBidFetch: Date | null = null;
  formattedLastBidFetch: string = '';

  // Sorting state
  stashBidsSortBy: string | null = 'amount';
  stashBidsSortOrder: 'asc' | 'desc' = 'desc';

  // Filtering state (active filters)
  stashBidsFilters: {
    bidder: string;
    source: string;
    amountMin: number | null;
    amountMax: number | null;
    confidential: boolean | null;
    bidType: string;
    status: string;
  } = {
    bidder: '',
    source: '',
    amountMin: null,
    amountMax: null,
    confidential: null,
    bidType: '',
    status: '',
  };

  // Temporary filter values (before applying)
  tempFilters: {
    bidder: string;
    source: string;
    amountMin: string;
    amountMax: string;
    confidential: boolean;
    bidType: string;
    status: string;
  } = {
    bidder: '',
    source: '',
    amountMin: '',
    amountMax: '',
    confidential: false,
    bidType: '',
    status: '',
  };

  // Available sources for dropdown (will be populated from bids)
  availableSources: { label: string; value: string }[] = [];

  // Available bid types for dropdown
  availableBidTypes: { label: string; value: string }[] = [
    { label: 'All Types', value: '' },
    { label: 'Trait Bid', value: 'trait' },
    { label: 'Collection Bid', value: 'collection' },
    { label: 'Custom Bid', value: 'custom' },
    { label: 'Owner Bid', value: 'owner' }
  ];
  availableBidStatuses: { label: string; value: string }[] = [
    { label: 'All Statuses', value: '' },
    { label: 'Active', value: 'active' },
    { label: 'Unfunded', value: 'unfunded' },
    { label: 'Expired', value: 'expired' },
    { label: 'Cancelled', value: 'cancelled' },
  ];

  // Active filter dropdown
  activeFilter: string | null = null;

  /** When true, API is called with includeAllStatuses (show non-pending / archived bids). */
  displayArchivedBids = false;
  private readonly STASH_BIDS_DISPLAY_ARCHIVED_STORAGE_KEY = 'market-stash-bids-display-archived';

  // Bid details modal state
  bidDetailsModalActive: boolean = false;
  selectedBidForDetails: StashBid | null = null;
  private currentBidDeepLinkId: string | null = null;
  /** Increments on each deep-link resolution or when leaving detail URL — cancels stale async fetches. */
  private bidDeepLinkSeq = 0;
  /** True while fetching a bid by id for `/bids/detail/:id` when it is not on the current list page. */
  bidDeepLinkLoading = false;
  bidDeepLinkError: string | null = null;
  private currentMarketType: Filters = 'all';

  /** Bids list tab when both Stash and PunksMarket are enabled. */
  bidsPlatformTab: 'stash' | 'punksmarket' = 'stash';
  pmBidsTableRows: StashBid[] = [];
  pmBidsLoading = false;
  pmBidsError: string | null = null;
  pmBidsCurrentPage = 1;
  pmBidsItemsPerPage = 10;
  pmBidsTotalPages = 1;
  pmBidsTotal = 0;
  pmBidsPaginationItems: CompactPaginationItem[] = [];

  // Copy bid (bid creation modal) state
  bidCreationModalActive: boolean = false;
  copyFromBidPayload: CopyFromBidPayload | null = null;
  copyConfirmationModalActive: boolean = false;
  copyConfirmationPunkIds: number[] = [];

  // ViewChild references for filter inputs
  @ViewChild('bidderInput') bidderInput?: ElementRef<HTMLInputElement>;
  @ViewChild('amountMinInput') amountMinInput?: ElementRef<HTMLInputElement>;
  @ViewChild('amountMaxInput') amountMaxInput?: ElementRef<HTMLInputElement>;
  @ViewChild('sourceSelect') sourceSelect?: NgSelectComponent;
  @ViewChild('bidTypeSelect') bidTypeSelect?: NgSelectComponent;
  @ViewChild('statusSelect') statusSelect?: NgSelectComponent;

  // Stash address computation constants
  private readonly STASH_FACTORY_ADDRESS = '0x000000000000A6fA31F5fC51c1640aAc76866750';
  // Calculate salt suffix from "GAGAGGA GEEEE GOGOGOGGGO" phrase (first 12 bytes of keccak256)
  // This matches the _salt() function in ERC721StashFactory.sol
  private readonly STASH_SALT_SUFFIX = (() => {
    const phrase = keccak256(stringToBytes('GAGAGGA GEEEE GOGOGOGGGO'));
    // Take first 12 bytes (24 hex chars) and convert to BigInt
    return BigInt(phrase.slice(0, 26));
  })();
  // Note: STASH_INIT_CODE_HASH is not used when using web3Service.getStashAddressFor()
  // which calls the contract directly. This is kept only as a fallback.
  // The correct value should be calculated from the ERC1967Factory proxy bytecode.
  private readonly STASH_INIT_CODE_HASH = '0xe5e58e5bf515959cfde70a49827f414a27af4ee79b57e5fd8cf060d9130fa9b9';

  protected readonly environment = environment;

  get stashBidsEnabled(): boolean {
    return environment.featureFlags?.STASH_BID_SUPPORT !== false;
  }

  get punksmarketBidsEnabled(): boolean {
    return environment.featureFlags?.PUNKSMARKET_BID_SUPPORT !== false;
  }

  get bidsSectionEnabled(): boolean {
    return this.stashBidsEnabled || this.punksmarketBidsEnabled;
  }

  constructor(
    public route: ActivatedRoute,
    private router: Router,
    public dataSvc: DataService,
    public stateSvc: StateService,
    private bidTagsService: BidTagsService,
    private web3Svc: Web3Service,
    private cdr: ChangeDetectorRef,
    private platformService: PlatformService,
  ) {
    this.marketType$ = this.route.params.pipe(
      map((res: any) => this.resolveMarketType(res)),
      distinctUntilChanged()
    );

    this.punkData$ = this.route.params.pipe(
      map((res: any) => this.resolveMarketType(res)),
      tap((res: any) => console.log('marketType', res)),
      switchMap((marketType: Filters) => {
        const params = this.route.snapshot.queryParams;

        if (marketType === 'owned') {
          const address = params.address?.toLowerCase();

          // Set redirecting flag to hide content
          this.isRedirecting = true;
          this.cdr.detectChanges();

          // Only redirect to NFTX vault on collections that actually support it.
          if (environment.collection !== 'cryptopunks-v1' && address === environment.nftxVaultAddress) {
            this.router.navigate(['/', 'nftx-vault'], { replaceUrl: true });
            return of([]);
          }
          
          if (address) {
            this.router.navigate(['/', 'user', address], { replaceUrl: true });
            return of([]);
          }
          // if (address) return this.dataSvc.fetchOwned(address);
          this.router.navigate(['/', 'listings'], { replaceUrl: true });
          return of([]);
        }
        
        // Reset redirecting flag for other market types
        this.isRedirecting = false;
        
        if (marketType === 'nftx-vault') {
          // CryptoPunks V1 does not have an NFTX pool; redirect to listings.
          if (environment.collection === 'cryptopunks-v1') {
            this.isRedirecting = true;
            this.cdr.detectChanges();
            this.router.navigate(['/', 'listings'], { replaceUrl: true });
            return of([]);
          }
          // console.log('is NFTX Vault');

          return this.dataSvc.fetchNftxVault(1000).pipe(
            // tap((res: Punk[]) => console.log('NFTX Vault', res.length))
          );
        }

        if (marketType === 'bids') {
          this.activeSort = 'price-high';
        }

        if (marketType === 'all') {
          this.activeSort = 'id';
          return this.dataSvc.getAllData();
        }

        if (marketType === 'sales') {
          console.log('[MarketComponent] Loading sales data...');
          this.activeSort = 'recent';
          console.log('[MarketComponent] About to subscribe to salesData$');
          return this.dataSvc.salesData$;
        }

        return this.dataSvc.marketData$;
      }),
    );

  }

  ngOnInit(): void {
    this.restoreStashBidsDisplayArchivedPreference();
    if (!this.stashBidsEnabled && this.punksmarketBidsEnabled) {
      this.bidsPlatformTab = 'punksmarket';
    }

    // Load stash bids when marketType is 'bids' (subscribe before queryParams so initial ?page= is applied once)
    this.marketType$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(marketType => {
      this.currentMarketType = marketType ?? 'all';
      if (marketType === 'bids') {
        if (this.bidsPlatformTab === 'stash' && this.stashBidsEnabled) {
          this.loadAllStashBids();
        } else if (this.bidsPlatformTab === 'punksmarket' && this.punksmarketBidsEnabled) {
          void this.loadPunksmarketBids();
        }
      }
    });

    this.route.queryParamMap
      .pipe(
        takeUntil(this.destroy$),
        skip(1),
        map((q) => q.get('page')),
        distinctUntilChanged(),
      )
      .subscribe((pageStr) => {
        if (!this.isStashBidsListView()) {
          return;
        }
        const page = this.parseStashBidsPageQuery(pageStr);
        if (page === this.stashBidsCurrentPage) {
          return;
        }
        this.stashBidsCurrentPage = page;
        void this.runLoadAllStashBids(true);
      });

    this.route.params
      .pipe(
        takeUntil(this.destroy$),
        map((params: any) => this.resolveBidId(params)),
        distinctUntilChanged()
      )
      .subscribe((bidId) => {
        this.currentBidDeepLinkId = bidId;

        if (!bidId) {
          this.bidDeepLinkSeq++;
          this.bidDeepLinkLoading = false;
          this.bidDeepLinkError = null;
          if (this.bidDetailsModalActive) {
            this.closeBidDetailsModal(false);
          }
          return;
        }

        void this.tryOpenBidDetailsFromDeepLink(bidId);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadAllStashBids(): Promise<void> {
    await this.runLoadAllStashBids(true);
  }

  /** True on `/bids` list, false on `/bids/detail/:id`. */
  private isStashBidsListView(): boolean {
    const params = this.route.snapshot.params as { marketType?: string; bidId?: string };
    if (this.resolveMarketType(params) !== 'bids') {
      return false;
    }
    return params.bidId == null || params.bidId === '';
  }

  private parseStashBidsPageQuery(pageStr: string | null): number {
    if (pageStr == null || pageStr === '') {
      return 1;
    }
    const p = parseInt(pageStr, 10);
    return !isNaN(p) && p >= 1 ? p : 1;
  }

  /** Apply `?page=` from the URL before each stash-bids fetch (deep links). */
  private applyStashBidsPageFromRouteSnapshot(): void {
    if (!this.isStashBidsListView()) {
      return;
    }
    const raw = this.route.snapshot.queryParamMap.get('page');
    if (raw == null || raw === '') {
      return;
    }
    const p = parseInt(raw, 10);
    if (!isNaN(p) && p >= 1) {
      this.stashBidsCurrentPage = p;
    }
  }

  /** Keep the URL in sync with `stashBidsCurrentPage` (omit `page` when 1). */
  private syncStashBidsPageQueryParam(options?: { replaceUrl?: boolean }): Promise<boolean> {
    if (!this.isStashBidsListView()) {
      return Promise.resolve(true);
    }
    const next = this.stashBidsCurrentPage <= 1 ? null : this.stashBidsCurrentPage;
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: next },
      queryParamsHandling: 'merge',
      replaceUrl: options?.replaceUrl ?? false,
    });
  }

  /**
   * Loads stash bids with server-side pagination and optional filterMarketBidType on the indexer.
   */
  private async runLoadAllStashBids(allowOutOfBoundsRetry: boolean): Promise<void> {
    this.applyStashBidsPageFromRouteSnapshot();
    this.stashBidsLoading = true;
    this.stashBidsError = null;

    const minLoadTime = 400;

    try {
      const params: any = {};
      if (this.stashBidsSortBy) {
        params.sortBy = this.stashBidsSortBy;
      }
      if (this.stashBidsSortOrder) {
        params.sortOrder = this.stashBidsSortOrder;
      }
      if (this.stashBidsFilters.bidder) {
        params.filterBidder = this.stashBidsFilters.bidder;
      }
      if (this.stashBidsFilters.source !== undefined) {
        params.filterSource = this.stashBidsFilters.source;
      }
      if (this.stashBidsFilters.amountMin !== null && this.stashBidsFilters.amountMin !== undefined) {
        params.filterAmountMin = this.stashBidsFilters.amountMin;
      }
      if (this.stashBidsFilters.amountMax !== null && this.stashBidsFilters.amountMax !== undefined) {
        params.filterAmountMax = this.stashBidsFilters.amountMax;
      }
      if (this.stashBidsFilters.confidential !== null && this.stashBidsFilters.confidential !== undefined) {
        params.filterConfidential = this.stashBidsFilters.confidential;
      }
      if (this.displayArchivedBids) {
        params.includeAllStatuses = true;
      }
      if (this.stashBidsFilters.bidType) {
        params.filterMarketBidType = this.stashBidsFilters.bidType;
      }
      if (this.stashBidsFilters.status) {
        params.filterStatus = this.stashBidsFilters.status;
      }
      params.page = this.stashBidsCurrentPage;
      params.limit = this.stashBidsItemsPerPage;

      const [response] = await Promise.all([
        firstValueFrom(this.dataSvc.fetchAllStashBids(params)),
        new Promise<void>(resolve => setTimeout(resolve, minLoadTime)),
      ]);

      let bids =
        response && typeof response === 'object' && 'bids' in response
          ? response.bids
          : Array.isArray(response)
            ? response
            : [];
      if (!Array.isArray(bids)) {
        bids = [];
      }

      const totalFromApi =
        response && typeof response === 'object' && typeof (response as { total?: unknown }).total === 'number'
          ? Number((response as { total: number }).total)
          : bids.length;

      this.lastBidFetch =
        response && typeof response === 'object' && 'lastBidFetch' in response && (response as { lastBidFetch?: string | Date }).lastBidFetch
          ? new Date((response as { lastBidFetch: string | Date }).lastBidFetch)
          : null;
      this.updateFormattedLastBidFetch();

      const getDefaultSources = (collection: string): string[] => {
        if (collection === 'cryptopunks') return ['cryptopunks.eth.limo', 'cryptopunks.app'];
        if (collection === 'cryptopunks-v1') return ['v1cryptopunks.com'];
        return ['cryptopunks.eth.limo'];
      };

      if (bids.length === 0) {
        this.allStashBids = [];
        this.individualStashBids = [];
        const defaultSources = getDefaultSources(environment.collection);
        this.availableSources = [
          { label: 'All Sources', value: '' },
          ...defaultSources.sort().map(s => ({ label: s, value: s })),
        ];
        this.stashBidsTotalPages =
          totalFromApi === 0 ? 0 : Math.ceil(totalFromApi / this.stashBidsItemsPerPage);
        if (
          allowOutOfBoundsRetry &&
          this.stashBidsTotalPages > 0 &&
          this.stashBidsCurrentPage > this.stashBidsTotalPages
        ) {
          this.stashBidsCurrentPage = 1;
          await this.syncStashBidsPageQueryParam({ replaceUrl: true });
          await this.runLoadAllStashBids(false);
          return;
        }
        this.initializeTempFilters();
        if (this.currentBidDeepLinkId) {
          void this.tryOpenBidDetailsFromDeepLink(this.currentBidDeepLinkId);
        }
        return;
      }

      // Process and enrich bids
      // Get stash addresses for all bidders in parallel
      const stashAddressPromises = bids.map((bid: any) => this.resolveStashAddressForBidder(bid?.bidder_address));
      const stashAddresses = await Promise.all(stashAddressPromises);

      this.allStashBids = bids.map((bid: any, index: number) =>
        this.enrichStashBidWithStashAddress(bid, stashAddresses[index])
      );

      const sources = new Set<string>();
      this.allStashBids.forEach(bid => {
        if (bid.source) {
          sources.add(bid.source);
        }
      });
      const defaultSources = getDefaultSources(environment.collection);
      defaultSources.forEach(source => sources.add(source));

      this.availableSources = [
        { label: 'All Sources', value: '' },
        ...Array.from(sources).sort().map(s => ({ label: s, value: s })),
      ];

      this.individualStashBids = this.allStashBids
        .filter(bid => {
          const punkIds = bid.selected_punk_ids || bid.punkIndices || 
                          (Array.isArray(bid.punk_indices) ? bid.punk_indices : 
                           (typeof bid.punk_indices === 'string' ? bid.punk_indices.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id)) : []));
          
          const hasPunkIndices = Array.isArray(punkIds) && punkIds.length > 0;
          return hasPunkIndices && bid.bidType !== 'trait' && bid.bidType !== 'collection';
        })
        .slice(0, 9);

      this.stashBidsTotalPages =
        totalFromApi === 0 ? 0 : Math.ceil(totalFromApi / this.stashBidsItemsPerPage);
      if (
        allowOutOfBoundsRetry &&
        this.stashBidsTotalPages > 0 &&
        this.stashBidsCurrentPage > this.stashBidsTotalPages
      ) {
        this.stashBidsCurrentPage = 1;
        await this.syncStashBidsPageQueryParam({ replaceUrl: true });
        await this.runLoadAllStashBids(false);
        return;
      }

      this.initializeTempFilters();

      if (this.currentBidDeepLinkId) {
        void this.tryOpenBidDetailsFromDeepLink(this.currentBidDeepLinkId);
      }
      
    } catch (error) {
      console.error('Error loading stash bids:', error);
      this.stashBidsError = 'Failed to load bids. Please try again later.';
      this.allStashBids = [];
      this.individualStashBids = [];
      this.stashBidsTotalPages = 0;
    } finally {
      this.stashBidsLoading = false;
      this.refreshStashBidsPaginationItems();
    }
  }

  switchBidsPlatformTab(tab: 'stash' | 'punksmarket'): void {
    if (this.bidsPlatformTab === tab) {
      return;
    }
    this.bidsPlatformTab = tab;
    if (tab === 'stash' && this.stashBidsEnabled) {
      void this.loadAllStashBids();
    } else if (tab === 'punksmarket' && this.punksmarketBidsEnabled) {
      void this.loadPunksmarketBids();
    }
  }

  async loadPunksmarketBids(): Promise<void> {
    this.pmBidsLoading = true;
    this.pmBidsError = null;
    try {
      const offset = (this.pmBidsCurrentPage - 1) * this.pmBidsItemsPerPage;
      const res = await firstValueFrom(
        this.dataSvc.fetchPunksmarketBids({
          active: true,
          limit: this.pmBidsItemsPerPage,
          offset,
          sort: 'bid_wei-desc',
        }),
      );
      this.pmBidsTotal = res.total ?? res.items.length;
      this.pmBidsTableRows = (res.items ?? []).map((item) => {
        const unified = mapPunksmarketIndexerBidToUnified(item);
        return unifiedBidToStashTableShape(unified) as StashBid;
      });
      this.pmBidsTotalPages =
        this.pmBidsTotal === 0
          ? 0
          : Math.ceil(this.pmBidsTotal / this.pmBidsItemsPerPage);
      this.pmBidsPaginationItems = buildCompactPaginationItems(
        this.pmBidsCurrentPage,
        this.pmBidsTotalPages,
      );
    } catch {
      this.pmBidsError = 'Failed to load PunksMarket bids.';
      this.pmBidsTableRows = [];
      this.pmBidsTotalPages = 0;
    } finally {
      this.pmBidsLoading = false;
      this.cdr.detectChanges();
    }
  }

  goToPmBidsPage(page: number): void {
    if (page < 1 || page > this.pmBidsTotalPages) {
      return;
    }
    this.pmBidsCurrentPage = page;
    void this.loadPunksmarketBids();
  }

  private refreshStashBidsPaginationItems(): void {
    this.stashBidsPaginationItems = buildCompactPaginationItems(
      this.stashBidsTotalPages,
      this.stashBidsCurrentPage,
    );
  }

  private normalizeHexAddress(address: string | null | undefined): string | null {
    if (!address) {
      return null;
    }
    const trimmed = address.trim();
    if (!trimmed) {
      return null;
    }
    const prefixed = trimmed.startsWith('0x') ? trimmed : `0x${trimmed}`;
    return /^0x[0-9a-fA-F]{40}$/.test(prefixed) ? prefixed : null;
  }

  private computeStashSalt(ownerAddress: string | null | undefined): `0x${string}` | null {
    const normalized = this.normalizeHexAddress(ownerAddress);
    if (!normalized) {
      return null;
    }
    try {
      const ownerValue = BigInt(normalized);
      const salt = (ownerValue << 96n) | this.STASH_SALT_SUFFIX;
      return `0x${salt.toString(16).padStart(64, '0')}` as `0x${string}`;
    } catch {
      return null;
    }
  }

  computeStashAddress(bidderAddress: string | null | undefined): string | null {
    const salt = this.computeStashSalt(bidderAddress);
    if (!salt) {
      return null;
    }
    const payload = `0xff${this.STASH_FACTORY_ADDRESS.slice(2)}${salt.slice(2)}${this.STASH_INIT_CODE_HASH.slice(2)}` as `0x${string}`;
    const hash = keccak256(payload);
    return `0x${hash.slice(-40)}`;
  }

  formatBiddingOn(bid: any, bidType: string, decodedTags: string[]): string {
    if (bidType === 'trait') {
      if (decodedTags.length > 0) {
        const displayTags = decodedTags.slice(0, 2);
        return displayTags.join(', ') + (decodedTags.length > 2 ? ` +${decodedTags.length - 2}` : '');
      }
      return 'Trait';
    }
    
    if (bidType === 'owner') {
      return 'Owner';
    }
    
    const punkIds = bid.selected_punk_ids || bid.punkIndices || 
                   (Array.isArray(bid.punk_indices) ? bid.punk_indices : 
                    (typeof bid.punk_indices === 'string' ? bid.punk_indices.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id)) : []));
    
    if (Array.isArray(punkIds) && punkIds.length > 0) {
      if (punkIds.length === 1) {
        return `Punk #${punkIds[0]}`;
      }
      return `Punk #${punkIds[0]} (+${punkIds.length - 1})`;
    }
    
    if (bidType === 'custom') {
      return 'Custom';
    }
    
    return 'Collection';
  }

  getPaginatedStashBids(): StashBid[] {
    return this.allStashBids;
  }

  async goToStashBidsPage(page: number): Promise<void> {
    if (page < 1 || page > this.stashBidsTotalPages) {
      return;
    }
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: page <= 1 ? null : page },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return '-';
    }
  }

  private updateFormattedLastBidFetch(): void {
    if (!this.lastBidFetch) {
      this.formattedLastBidFetch = '';
      return;
    }
    try {
      const dateString = this.lastBidFetch.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      const relativeTime = formatDistanceToNow(this.lastBidFetch, { 
        addSuffix: true
      });
      this.formattedLastBidFetch = `${dateString} (${relativeTime})`;
    } catch {
      this.formattedLastBidFetch = '';
    }
  }

  getBidTypeDisplay(bid: StashBid): string {
    return bid.bidType === 'trait' ? 'Trait' : 
           bid.bidType === 'owner' ? 'Owner' :
           bid.bidType === 'custom' ? 'Individual' :
           'Individual';
  }

  onStashBidsSort(column: string): void {
    // If clicking the same column, toggle sort order
    if (this.stashBidsSortBy === column) {
      this.stashBidsSortOrder = this.stashBidsSortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, default to descending
      this.stashBidsSortBy = column;
      this.stashBidsSortOrder = 'desc';
    }
    
    // Reset to page 1 and reload
    this.stashBidsCurrentPage = 1;
    void this.syncStashBidsPageQueryParam({ replaceUrl: true }).then(() => this.loadAllStashBids());
  }

  onStashBidsFilterChange(filterName: string, value: any): void {
    (this.tempFilters as any)[filterName] = value;
  }

  async applyStashBidsFilters(): Promise<void> {
    // Convert temp filters to active filters
    // Handle ENS name conversion for bidder filter
    let bidderValue = this.tempFilters.bidder?.trim() || '';
    if (bidderValue) {
      const isEns = bidderValue.includes('.eth');
      if (isEns) {
        // Convert ENS name to address
        const resolvedAddress = await this.web3Svc.getEnsOwner(bidderValue);
        if (resolvedAddress) {
          bidderValue = resolvedAddress;
        } else {
          // If ENS resolution fails, keep the original value but it won't match anything
          console.warn(`Failed to resolve ENS name: ${bidderValue}`);
        }
      } else {
        // Verify it's a valid address format (partial match is allowed)
        const normalized = bidderValue.toLowerCase();
        bidderValue = normalized;
      }
    }
    
    this.stashBidsFilters.bidder = bidderValue;
    this.stashBidsFilters.source = this.tempFilters.source;
    this.stashBidsFilters.amountMin = this.tempFilters.amountMin ? parseFloat(this.tempFilters.amountMin) : null;
    this.stashBidsFilters.amountMax = this.tempFilters.amountMax ? parseFloat(this.tempFilters.amountMax) : null;
    // Only send filterConfidential to backend when toggle is active (true)
    // When false, don't send the parameter so backend returns all bids
    this.stashBidsFilters.confidential = this.tempFilters.confidential ? true : null;
    this.stashBidsFilters.bidType = this.tempFilters.bidType || '';
    this.stashBidsFilters.status = this.tempFilters.status || '';
    
    // Keep filter dropdown open so user can continue adjusting filters
    
    // Reset to page 1 and reload
    this.stashBidsCurrentPage = 1;
    await this.syncStashBidsPageQueryParam({ replaceUrl: true });
    await this.loadAllStashBids();
  }

  clearStashBidsFilters(): void {
    this.tempFilters = {
      bidder: '',
      source: '',
      amountMin: '',
      amountMax: '',
      confidential: false,
      bidType: '',
      status: '',
    };
    this.stashBidsFilters = {
      bidder: '',
      source: '',
      amountMin: null,
      amountMax: null,
      confidential: null,
      bidType: '',
      status: '',
    };
    // Close filter dropdown
    this.activeFilter = null;
    // Reset to page 1 and reload
    this.stashBidsCurrentPage = 1;
    void this.syncStashBidsPageQueryParam({ replaceUrl: true }).then(() => this.loadAllStashBids());
  }

  /** Reset archive toggle + all column filters, then reload. */
  clearAllStashBidsFilters(): void {
    this.displayArchivedBids = false;
    this.platformService.setLocalStorageItem(
      this.STASH_BIDS_DISPLAY_ARCHIVED_STORAGE_KEY,
      'false',
    );
    this.clearStashBidsFilters();
  }

  onFilterKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.applyStashBidsFilters();
    }
  }

  initializeTempFilters(): void {
    this.tempFilters.bidder = this.stashBidsFilters.bidder;
    // Ensure source matches the format expected by ng-select
    this.tempFilters.source = this.stashBidsFilters.source || '';
    this.tempFilters.amountMin = this.stashBidsFilters.amountMin?.toString() || '';
    this.tempFilters.amountMax = this.stashBidsFilters.amountMax?.toString() || '';
    this.tempFilters.confidential = this.stashBidsFilters.confidential === true;
    this.tempFilters.bidType = this.stashBidsFilters.bidType || '';
    this.tempFilters.status = this.stashBidsFilters.status || '';
  }

  getSortIcon(column: string): string {
    if (this.stashBidsSortBy !== column) {
      return '↕'; // Neutral icon when not sorted
    }
    return this.stashBidsSortOrder === 'asc' ? '↑' : '↓';
  }

  isColumnSorted(column: string): boolean {
    return this.stashBidsSortBy === column;
  }

  parseFloat(value: string): number | null {
    if (!value || value.trim() === '') {
      return null;
    }
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }

  onDisplayArchivedBidsToggle(): void {
    this.displayArchivedBids = !this.displayArchivedBids;
    this.platformService.setLocalStorageItem(
      this.STASH_BIDS_DISPLAY_ARCHIVED_STORAGE_KEY,
      String(this.displayArchivedBids),
    );
    this.stashBidsCurrentPage = 1;
    void this.syncStashBidsPageQueryParam({ replaceUrl: true }).then(() => this.loadAllStashBids());
  }

  private restoreStashBidsDisplayArchivedPreference(): void {
    if (!this.platformService.isClient) {
      return;
    }
    const saved = this.platformService.getLocalStorageItem(this.STASH_BIDS_DISPLAY_ARCHIVED_STORAGE_KEY);
    if (saved === 'true') {
      this.displayArchivedBids = true;
    } else if (saved === 'false') {
      this.displayArchivedBids = false;
    }
  }

  toggleFilter(column: string): void {
    if (this.activeFilter === column) {
      this.activeFilter = null;
    } else {
      this.activeFilter = column;
      // Force change detection and then focus the input
      this.cdr.detectChanges();
      setTimeout(() => {
        this.focusFilterInput(column);
      }, 0);
    }
  }

  private focusFilterInput(column: string): void {
    switch (column) {
      case 'bidder':
        if (this.bidderInput?.nativeElement) {
          this.bidderInput.nativeElement.focus();
        }
        break;
      case 'amount':
        // Focus on min input first, or max if min already has value
        if (this.amountMinInput?.nativeElement) {
          if (!this.tempFilters.amountMin) {
            this.amountMinInput.nativeElement.focus();
          } else if (this.amountMaxInput?.nativeElement) {
            this.amountMaxInput.nativeElement.focus();
          }
        }
        break;
      case 'source':
        if (this.sourceSelect) {
          // Open the dropdown for ng-select
          this.sourceSelect.open();
        }
        break;
      case 'bidType':
        if (this.bidTypeSelect) {
          // Open the dropdown for ng-select
          this.bidTypeSelect.open();
        }
        break;
      case 'status':
        if (this.statusSelect) {
          this.statusSelect.open();
        }
        break;
    }
  }

  isFilterActive(column: string): boolean {
    switch (column) {
      case 'source':
        return !!this.stashBidsFilters.source;
      case 'bidder':
        return !!this.stashBidsFilters.bidder;
      case 'amount':
        return this.stashBidsFilters.amountMin !== null || this.stashBidsFilters.amountMax !== null;
      case 'bidType':
        return !!this.stashBidsFilters.bidType || this.stashBidsFilters.confidential === true;
      case 'status':
        return !!this.stashBidsFilters.status;
      default:
        return false;
    }
  }

  clearFilter(column: string): void {
    switch (column) {
      case 'source':
        this.tempFilters.source = '';
        this.stashBidsFilters.source = '';
        break;
      case 'bidder':
        this.tempFilters.bidder = '';
        this.stashBidsFilters.bidder = '';
        break;
      case 'amount':
        this.tempFilters.amountMin = '';
        this.tempFilters.amountMax = '';
        this.stashBidsFilters.amountMin = null;
        this.stashBidsFilters.amountMax = null;
        break;
      case 'bidType':
        this.tempFilters.bidType = '';
        this.tempFilters.confidential = false;
        this.stashBidsFilters.bidType = '';
        this.stashBidsFilters.confidential = null;
        break;
      case 'status':
        this.tempFilters.status = '';
        this.stashBidsFilters.status = '';
        break;
    }
    
    // Reset to page 1 and reload
    this.stashBidsCurrentPage = 1;
    void this.syncStashBidsPageQueryParam({ replaceUrl: true }).then(() => this.loadAllStashBids());
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Close filter dropdown if clicking outside
    const target = event.target as HTMLElement;
    if (!target.closest('.filter-icon') && !target.closest('.filter-dropdown')) {
      this.activeFilter = null;
    }
  }

  // Copy bid JSON data to clipboard
  copyBidJson(bid: StashBid, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    try {
      const jsonString = JSON.stringify(bid, null, 2);
      navigator.clipboard.writeText(jsonString).then(() => {
        // Show temporary feedback
        const target = event.target as HTMLElement;
        const originalText = target.textContent;
        target.textContent = 'Copied!';
        target.style.color = '#00ff00';
        
        setTimeout(() => {
          target.textContent = originalText;
          target.style.color = '';
        }, 1000);
      }).catch(err => {
        console.error('Failed to copy bid JSON:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = jsonString;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      });
    } catch (error) {
      console.error('Error serializing bid data:', error);
    }
  }

  private static readonly COPY_BID_AMOUNT_INCREMENT_ETH = 0.01;
  private static readonly EXPIRY_DAY_OPTIONS = [1, 3, 7, 30, 365];

  openCopyBidModal(bid: StashBid): void {
    this.dataSvc.fetchBidDetailsWithPunkIndices(bid.id).subscribe({
      next: (bidDetails: any) => {
        this.buildCopyBidPayload(bid, bidDetails);
      },
      error: () => {
        this.buildCopyBidPayload(bid, null);
      }
    });
  }

  private buildCopyBidPayload(bid: StashBid, bidDetails: any): void {
    const modalBidType: BidType = bid.bidType === 'custom' ? 'targeted'
      : (bid.bidType === 'owner' || bid.bidType === 'trait' || bid.bidType === 'collection')
        ? bid.bidType
        : 'collection';

    let punkIndices: number[] = [];
    let selectedPunkIds: number[] = [];

    const detailPunkIds = this.extractPunkIdsFromDetails(bidDetails);

    if (modalBidType === 'owner' || modalBidType === 'trait') {
      punkIndices = detailPunkIds.length > 0 ? detailPunkIds : (bid.punkIds ?? bid.punkIndices ?? []);
    } else if (modalBidType === 'targeted') {
      if (detailPunkIds.length > 0) {
        selectedPunkIds = detailPunkIds;
      } else {
        const rawIds = bid.selected_punk_ids ?? bid.punkIds ?? bid.punkIndices ?? [];
        selectedPunkIds = Array.isArray(rawIds)
          ? rawIds
          : typeof rawIds === 'string'
            ? rawIds.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n))
            : [];
      }
    }

    let expiryDays: number | undefined;
    if (bid.expires_at && bid.created_at) {
      const diffMs = new Date(bid.expires_at).getTime() - new Date(bid.created_at).getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays > 0) {
        expiryDays = MarketComponent.EXPIRY_DAY_OPTIONS.reduce((prev, curr) =>
          Math.abs(curr - diffDays) < Math.abs(prev - diffDays) ? curr : prev
        );
      }
    }

    this.copyFromBidPayload = {
      amountEth: bid.bid_amount_eth + MarketComponent.COPY_BID_AMOUNT_INCREMENT_ETH,
      expiryDays,
      confidential: bid.confidential ?? false,
      bidType: modalBidType,
      ownerAddress: bid.ownerAddress ?? null,
      punkIndices,
      selectedPunkIds,
      activeTraits: bid.decodedTags ?? [],
      filterTag: bid.tag ?? ''
    };

    const allPunkIds = [...punkIndices, ...selectedPunkIds];
    if (modalBidType !== 'collection' && allPunkIds.length > 0) {
      this.copyConfirmationPunkIds = allPunkIds;
      this.copyConfirmationModalActive = true;
    } else {
      this.bidCreationModalActive = true;
    }
  }

  private extractPunkIdsFromDetails(bidDetails: any): number[] {
    if (!bidDetails?.parsed_punk_indices) return [];
    if (bidDetails.parsed_punk_indices === 'all') return [];
    if (Array.isArray(bidDetails.parsed_punk_indices)) return bidDetails.parsed_punk_indices;
    return [];
  }

  onCopyConfirmed(): void {
    this.copyConfirmationModalActive = false;
    this.copyConfirmationPunkIds = [];
    this.bidCreationModalActive = true;
  }

  onCopyCancelled(): void {
    this.copyConfirmationModalActive = false;
    this.copyConfirmationPunkIds = [];
    this.copyFromBidPayload = null;
  }

  closeCopyBidModal(): void {
    this.bidCreationModalActive = false;
    this.copyFromBidPayload = null;
  }

  onCopyBidCreated(): void {
    this.closeCopyBidModal();
    this.loadAllStashBids();
  }

  onShowBidDetails(bid: StashBid): void {
    this.openBidDetailsModal(bid, true);
  }

  private openBidDetailsModal(bid: StashBid, syncUrl: boolean): void {
    this.selectedBidForDetails = bid;
    this.bidDetailsModalActive = true;

    if (!syncUrl || this.currentMarketType !== 'bids') {
      return;
    }

    const targetBidId = String(bid.id);
    if (this.currentBidDeepLinkId === targetBidId) {
      return;
    }

    this.router.navigate(['/', 'bids', 'detail', targetBidId], {
      queryParamsHandling: 'preserve'
    });
  }

  closeBidDetailsModal(syncUrl: boolean = true): void {
    this.bidDetailsModalActive = false;
    this.selectedBidForDetails = null;

    if (!syncUrl || this.currentMarketType !== 'bids' || !this.currentBidDeepLinkId) {
      return;
    }

    this.router.navigate(['/', 'bids'], {
      queryParamsHandling: 'preserve'
    });
  }

  trackByPunkId(_index: number, punkId: number): number {
    return punkId;
  }

  private async resolveStashAddressForBidder(bidderAddress: string | null | undefined): Promise<string | null> {
    if (!bidderAddress) {
      return null;
    }
    try {
      return await this.web3Svc.getStashAddressFor(bidderAddress);
    } catch (error) {
      console.warn(`Failed to get stash address for ${bidderAddress}:`, error);
      return this.computeStashAddress(bidderAddress);
    }
  }

  private enrichStashBidWithStashAddress(bid: any, stashAddress: string | null): StashBid {
    const decodedTags = this.bidTagsService.decodeCollectionBidTags(bid);
    const bidType = this.bidTagsService.getCollectionBidType(bid, decodedTags);

    let ownerAddress: string | undefined;
    if (bidType === 'owner' && decodedTags.length > 0) {
      const ownerTag = decodedTags[0];
      const match = ownerTag.match(/^Owner:\s*(0x[a-fA-F0-9]{40})$/i);
      if (match) {
        ownerAddress = match[1].toLowerCase();
      }
    }

    const biddingOn = this.formatBiddingOn(bid, bidType, decodedTags);

    let punkIds: number[] = [];
    try {
      if (bid.selected_punk_ids) {
        punkIds = Array.isArray(bid.selected_punk_ids)
          ? bid.selected_punk_ids
          : typeof bid.selected_punk_ids === 'string'
            ? bid.selected_punk_ids.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id))
            : [];
      } else if (bid.punkIndices) {
        punkIds = Array.isArray(bid.punkIndices) ? bid.punkIndices : [];
      } else if (bid.punk_indices) {
        if (typeof bid.punk_indices === 'string') {
          punkIds = JSON.parse(bid.punk_indices);
        } else if (Array.isArray(bid.punk_indices)) {
          punkIds = bid.punk_indices;
        }
      }
    } catch (error) {
      console.error('Error parsing punk indices:', error);
    }
    const firstPunkId = Array.isArray(punkIds) && punkIds.length > 0 ? punkIds[0] : undefined;
    const punkPreviewIds = Array.isArray(punkIds) ? punkIds.slice(0, 3) : [];
    const punkExtraCount = Math.max(0, (punkIds?.length || 0) - punkPreviewIds.length);
    const punkIndicesCount = bid.punk_indices_count != null ? Number(bid.punk_indices_count) : (Array.isArray(punkIds) ? punkIds.length : 0);
    const displayPreviewIds = punkPreviewIds.length > 0 ? punkPreviewIds : (punkIndicesCount > 0 && Array.isArray(punkIds) ? punkIds.slice(0, 3) : []);
    const displayExtraCount = punkExtraCount || (punkIndicesCount > 3 ? punkIndicesCount - 3 : 0);

    const uiStatus = this.mapBidStatusForUi(bid.status, bid.log);
    return {
      ...bid,
      decodedTags,
      bidType,
      stashAddress,
      ownerAddress,
      biddingOn,
      firstPunkId,
      punkIds,
      punkPreviewIds: displayPreviewIds,
      punkExtraCount: displayExtraCount,
      punk_indices_count: punkIndicesCount,
      bidTypeLabel: bidType === 'owner' ? 'Owner Bid' :
        bidType === 'custom' ? 'Custom Bid' :
          bidType === 'trait' ? 'Trait Bid' :
            'Collection Bid',
      stash_funded: normalizeStashFundedForUi(bid.stash_funded),
      uiStatusLabel: uiStatus.label,
      uiStatusFilter: uiStatus.filterValue,
    };
  }

  /**
   * Opens bid details for `/bids/detail/:id` using the current list page when possible,
   * otherwise GET /punk-bids/:id so archived or off-page bids still open.
   */
  private async tryOpenBidDetailsFromDeepLink(bidId: string): Promise<void> {
    if (!bidId) {
      return;
    }

    if (this.bidDetailsModalActive && String(this.selectedBidForDetails?.id) === String(bidId)) {
      this.bidDeepLinkLoading = false;
      this.bidDeepLinkError = null;
      return;
    }

    const matchingBid = this.allStashBids.find((bid) => String(bid.id) === String(bidId));
    if (matchingBid) {
      this.bidDeepLinkLoading = false;
      this.bidDeepLinkError = null;
      this.openBidDetailsModal(matchingBid, false);
      return;
    }

    const pmRow = this.pmBidsTableRows.find((bid) => String(bid.id) === String(bidId));
    if (pmRow) {
      this.bidDeepLinkLoading = false;
      this.bidDeepLinkError = null;
      this.openBidDetailsModal(pmRow, false);
      return;
    }

    const pmId = String(bidId).replace(/^pm-/, '');
    if (pmId && /^\d+$/.test(pmId) && this.punksmarketBidsEnabled) {
      const seqPm = ++this.bidDeepLinkSeq;
      this.bidDeepLinkLoading = true;
      try {
        const item = await firstValueFrom(this.dataSvc.fetchPunksmarketBidById(pmId));
        if (seqPm !== this.bidDeepLinkSeq) {
          return;
        }
        if (item) {
          const row = unifiedBidToStashTableShape(
            mapPunksmarketIndexerBidToUnified(item),
          ) as StashBid;
          this.bidDeepLinkLoading = false;
          this.openBidDetailsModal(row, false);
          return;
        }
      } catch {
        /* fall through */
      }
    }

    const seq = ++this.bidDeepLinkSeq;
    this.bidDeepLinkLoading = true;
    this.bidDeepLinkError = null;

    try {
      const res = await firstValueFrom(this.dataSvc.fetchCollectionBidDetails(bidId));
      if (seq !== this.bidDeepLinkSeq) {
        return;
      }

      if (
        !res ||
        typeof res !== 'object' ||
        ('error' in res && (res as { error?: string }).error) ||
        (res as { id?: string }).id == null ||
        (res as { id?: string }).id === ''
      ) {
        const msg = (res as { error?: string })?.error || 'Bid not found';
        this.bidDeepLinkError = msg;
        this.bidDeepLinkLoading = false;
        return;
      }

      const stashAddress = await this.resolveStashAddressForBidder((res as any).bidder_address);
      if (seq !== this.bidDeepLinkSeq) {
        return;
      }

      const enriched = this.enrichStashBidWithStashAddress(res, stashAddress);
      this.bidDeepLinkLoading = false;
      this.bidDeepLinkError = null;
      this.openBidDetailsModal(enriched, false);
    } catch (e) {
      console.error('Deep link bid fetch failed:', e);
      if (seq !== this.bidDeepLinkSeq) {
        return;
      }
      this.bidDeepLinkError = 'Failed to load bid';
      this.bidDeepLinkLoading = false;
    }
  }

  retryBidDeepLink(): void {
    if (this.currentBidDeepLinkId) {
      void this.tryOpenBidDetailsFromDeepLink(this.currentBidDeepLinkId);
    }
  }

  clearBidDeepLinkError(): void {
    this.bidDeepLinkError = null;
  }

  private resolveMarketType(params: any): Filters {
    if (params?.marketType) {
      return params.marketType as Filters;
    }

    if (this.router.url.startsWith('/bids/detail/')) {
      return 'bids';
    }

    return 'all';
  }

  private resolveBidId(params: any): string | null {
    const bidId = params?.bidId;
    return bidId ? String(bidId) : null;
  }

  private mapBidStatusForUi(rawStatus?: string | null, rawLog?: string | null): { label: string; filterValue: string } {
    const status = (rawStatus || '').trim().toLowerCase();
    const log = (rawLog || '').toLowerCase();
    if (status === 'pending') {
      return { label: 'Active', filterValue: 'active' };
    }
    if (status === 'invalid') {
      if (log.includes('expire')) {
        return { label: 'Expired', filterValue: 'expired' };
      }
      return { label: 'Cancelled', filterValue: 'cancelled' };
    }
    if (status) {
      return {
        label: status.charAt(0).toUpperCase() + status.slice(1),
        filterValue: status,
      };
    }
    return { label: '-', filterValue: '' };
  }
}
